import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  TextField,
} from "@mui/material";
import { Visibility, Reply, DeleteOutline } from "@mui/icons-material";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../services/firebase";

interface Inquiry {
  id: string;
  content: string;
  email: string;
  // source未設定の既存ドキュメントはアプリ由来（サイト対応前はアプリからのみ送信されていたため）
  source: "app" | "web";
  timestamp: { _seconds: number };
  createdAt: { _seconds: number };
  status: "pending" | "in_progress" | "resolved";
  replied: boolean;
  replyNote?: string;
  // 端末情報（アプリv2.6.0以降からの送信のみ。旧アプリとサイトからは null）
  platform: string | null;
  osVersion: string | null;
  deviceModel: string | null;
  appVersion: string | null;
}

const InquiryList: React.FC = () => {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [replyNote, setReplyNote] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Inquiry | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchInquiries = useCallback(async () => {
    try {
      setLoading(true);
      // Firestore から直接取得
      const q = query(
        collection(db, "customerInquiries"),
        orderBy("createdAt", "desc"),
        limit(100)
      );
      const snapshot = await getDocs(q);
      const inquiryList = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const createdAt = data.createdAt as Timestamp | null;
        const timestamp = data.timestamp as Timestamp | null;
        return {
          id: docSnap.id,
          content: data.content,
          email: data.email || "",
          source: data.source === "web" ? "web" : "app",
          status: data.status || "pending",
          replied: data.replied || false,
          replyNote: data.replyNote,
          platform: data.platform || null,
          osVersion: data.osVersion || null,
          deviceModel: data.deviceModel || null,
          appVersion: data.appVersion || null,
          createdAt: createdAt
            ? { _seconds: createdAt.seconds }
            : { _seconds: Math.floor(Date.now() / 1000) },
          timestamp: timestamp
            ? { _seconds: timestamp.seconds }
            : { _seconds: Math.floor(Date.now() / 1000) },
        } as Inquiry;
      });
      setInquiries(inquiryList);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "エラーが発生しました");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleOpenDialog = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setReplyNote(inquiry.replyNote || "");
    setDialogOpen(true);

    // ダイアログを開いたら対応中にする
    if (inquiry.status === "pending") {
      updateInquiryStatus(inquiry.id, "in_progress", false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedInquiry(null);
    setReplyNote("");
  };

  const updateInquiryStatus = async (
    inquiryId: string,
    status: "in_progress" | "resolved",
    replied: boolean,
    note?: string
  ) => {
    try {
      // Firestore に直接更新
      const docRef = doc(db, "customerInquiries", inquiryId);
      if (note !== undefined) {
        await updateDoc(docRef, {
          status,
          replied,
          updatedAt: serverTimestamp(),
          replyNote: note,
        });
      } else {
        await updateDoc(docRef, {
          status,
          replied,
          updatedAt: serverTimestamp(),
        });
      }

      // ローカルステートを更新
      setInquiries((prev) =>
        prev.map((inquiry) =>
          inquiry.id === inquiryId
            ? { ...inquiry, status, replied, replyNote: note || inquiry.replyNote }
            : inquiry
        )
      );

      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry({
          ...selectedInquiry,
          status,
          replied,
          replyNote: note || selectedInquiry.replyNote,
        });
      }
    } catch (err) {
      console.error("Error updating inquiry status:", err);
      alert("ステータスの更新に失敗しました");
    }
  };

  const handleMarkAsReplied = async () => {
    if (selectedInquiry) {
      await updateInquiryStatus(selectedInquiry.id, "resolved", true, replyNote);
      handleCloseDialog();
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteDoc(doc(db, "customerInquiries", deleteTarget.id));
      setInquiries((prev) => prev.filter((inquiry) => inquiry.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Error deleting inquiry:", err);
      alert("削除に失敗しました");
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "未対応";
      case "in_progress":
        return "対応中";
      case "resolved":
        return "対応済み";
      default:
        return status;
    }
  };

  const getStatusColor = (
    status: string
  ): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case "pending":
        return "error";
      case "in_progress":
        return "warning";
      case "resolved":
        return "success";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            お問い合わせ
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            アプリとサイトからのお問い合わせの一覧です
          </Typography>

          <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>ステータス</TableCell>
                  <TableCell>送信元</TableCell>
                  <TableCell>投稿日時</TableCell>
                  <TableCell>メールアドレス</TableCell>
                  <TableCell>内容（プレビュー）</TableCell>
                  <TableCell align="center" sx={{ minWidth: 120 }}>
                    操作
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      お問い合わせはありません
                    </TableCell>
                  </TableRow>
                ) : (
                  inquiries.map((inquiry) => (
                    <TableRow key={inquiry.id}>
                      <TableCell>
                        <Box display="flex" gap={1} flexDirection="column">
                          <Chip
                            label={getStatusLabel(inquiry.status)}
                            color={getStatusColor(inquiry.status)}
                            size="small"
                          />
                          {inquiry.replied && (
                            <Chip label="返信済み" color="info" size="small" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={inquiry.source === "web" ? "サイト" : "アプリ"}
                          color={inquiry.source === "web" ? "secondary" : "default"}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(inquiry.createdAt._seconds * 1000).toLocaleString("ja-JP")}
                      </TableCell>
                      <TableCell>{inquiry.email}</TableCell>
                      <TableCell>
                        {inquiry.content.length > 50
                          ? `${inquiry.content.substring(0, 50)}...`
                          : inquiry.content}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenDialog(inquiry)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => setDeleteTarget(inquiry)}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 詳細ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          お問い合わせ詳細
          {selectedInquiry && (
            <>
              <Chip
                label={getStatusLabel(selectedInquiry.status)}
                color={getStatusColor(selectedInquiry.status)}
                size="small"
                sx={{ ml: 2 }}
              />
              {selectedInquiry.replied && (
                <Chip
                  label="返信済み"
                  color="info"
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )}
              <Chip
                label={selectedInquiry.source === "web" ? "サイト" : "アプリ"}
                color={selectedInquiry.source === "web" ? "secondary" : "default"}
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            </>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedInquiry && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                投稿日時
              </Typography>
              <Typography variant="body1" paragraph>
                {new Date(selectedInquiry.createdAt._seconds * 1000).toLocaleString("ja-JP")}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                メールアドレス
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedInquiry.email}
              </Typography>

              {selectedInquiry.platform && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    端末情報
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedInquiry.deviceModel || "不明"} / {selectedInquiry.platform}{" "}
                    {selectedInquiry.osVersion || ""} / アプリ v
                    {selectedInquiry.appVersion || "不明"}
                  </Typography>
                </>
              )}

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                お問い合わせ内容
              </Typography>
              <Paper sx={{ p: 2, bgcolor: "grey.50", mb: 2 }}>
                <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
                  {selectedInquiry.content}
                </Typography>
              </Paper>

              <TextField
                label="対応メモ（任意）"
                multiline
                rows={3}
                fullWidth
                value={replyNote}
                onChange={(e) => setReplyNote(e.target.value)}
                placeholder="メールで返信した内容や対応内容を記録できます"
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedInquiry && !selectedInquiry.replied && (
            <Button
              startIcon={<Reply />}
              variant="contained"
              color="primary"
              onClick={handleMarkAsReplied}
            >
              返信済みにする
            </Button>
          )}
          <Button onClick={handleCloseDialog}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>お問い合わせを削除しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            この操作は取り消せません。イタズラなど不要な投稿を完全に削除します。
          </Typography>
          {deleteTarget && (
            <Paper sx={{ p: 2, bgcolor: "grey.50", mt: 2 }}>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                {deleteTarget.content.length > 200
                  ? `${deleteTarget.content.substring(0, 200)}...`
                  : deleteTarget.content}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InquiryList;
